import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User, Star, LogOut, ArrowRight, Users } from 'lucide-react';
import { BACKEND_URL } from '../config/api.config';
import { useOrganizacion } from '../context/OrganizacionContext';

const EmpleadoDashboard = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [liderInfo, setLiderInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { config, logoUrl } = useOrganizacion();

  useEffect(() => {
    // console.log('[DASHBOARD DEBUG] EmpleadoDashboard montado');
    
    // Verificar autenticación
    const token = localStorage.getItem('access_token');
    const storedUserInfo = localStorage.getItem('user_info');

    // console.log('[DASHBOARD DEBUG] Token existe:', !!token);
    // console.log('[DASHBOARD DEBUG] storedUserInfo existe:', !!storedUserInfo);
    // console.log('[DASHBOARD DEBUG] storedUserInfo raw:', storedUserInfo);

    if (!token || !storedUserInfo) {
      // console.log('[DASHBOARD DEBUG] No hay token o user_info, redirigiendo a /');
      toast.error('Sesión no válida. Por favor inicie sesión nuevamente.');
      navigate('/');
      return;
    }

    const user = JSON.parse(storedUserInfo);
    // console.log('[DASHBOARD DEBUG] user parseado:', user);
    // console.log('[DASHBOARD DEBUG] user.tipo:', user.tipo);
    // console.log('[DASHBOARD DEBUG] Tipo de dato:', typeof user.tipo);
    
    // Verificar que sea un empleado O un líder intermedio actuando como empleado
    if (user.tipo !== 'employee') {
      // console.log('[DASHBOARD DEBUG] user.tipo NO es "employee", redirigiendo a /');
      toast.error('Acceso no autorizado');
      navigate('/');
      return;
    }

    // console.log('[DASHBOARD DEBUG] Autenticación exitosa, cargando dashboard');
    setUserInfo(user);
    fetchLiderInfo(user.id, token);
  }, [navigate]);

  const fetchLiderInfo = async (empleadoId, token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/empleados-info/${empleadoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al obtener información del líder');
      }

      const data = await response.json();
      
      if (data.lider) {
        setLiderInfo(data.lider);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('No se pudo cargar la información de su líder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEvaluarLider = () => {
    if (!liderInfo) {
      toast.error('No se encontró información del líder');
      return;
    }

    // Preparar datos para la evaluación
    const evaluacionData = {
      evaluador: {
        user_id: userInfo.id,
        user_name: userInfo.nombre,
        user_type: 'empleado',
        cargo: 'Empleado'
      },
      evaluado: {
        id: liderInfo.id,
        nombre: liderInfo.nombre_completo,
        cargo: liderInfo.cargo || 'Líder'
      }
    };

    localStorage.setItem('evaluacionData', JSON.stringify(evaluacionData));
    navigate('/evaluacion-lider-dinamica');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('evaluacionData');
    toast.success('Sesión cerrada exitosamente');
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Logo de la organización */}
              {logoUrl && config.mostrar_logo && (
                <img
                  src={logoUrl}
                  alt={config.nombre_organizacion || 'Logo'}
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div 
                className="rounded-full p-3 mr-4"
                style={{ backgroundColor: `${config.color_primario}20` }}
              >
                <User className="h-8 w-8" style={{ color: config.color_primario }} />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold"
                  style={{ color: config.color_primario }}
                >
                  ¡Bienvenido, {userInfo?.nombre}!
                </h1>
                <p className="text-gray-600">Panel de Empleado</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Botón para cambiar a Dashboard de Líder (solo si es líder intermedio) */}
              {userInfo?.es_lider_intermedio && (
                <button
                  onClick={() => navigate('/role-selection')}
                  className="flex items-center px-4 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                  title="Cambiar a Dashboard de Líder"
                >
                  <Users className="h-5 w-5 mr-2" />
                  Cambiar Rol
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="h-5 w-5 mr-2" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>

        {/* Información del Usuario */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Mi Información</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Cédula</p>
              <p className="font-medium text-gray-900">{userInfo?.cedula}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium text-gray-900">{userInfo?.email || 'No registrado'}</p>
            </div>
          </div>
        </div>

        {/* Evaluación del Líder */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Star className="h-5 w-5 mr-2 text-yellow-500" />
            Evaluación de Desempeño
          </h2>

          {liderInfo ? (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Tu líder asignado:</p>
                <p className="font-semibold text-gray-900 text-lg">{liderInfo.nombre_completo}</p>
                {liderInfo.cargo && (
                  <p className="text-sm text-gray-600 mt-1">{liderInfo.cargo}</p>
                )}
              </div>

              <button
                onClick={handleEvaluarLider}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center group"
              >
                <span className="font-medium">Evaluar a mi Líder</span>
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>

              <p className="text-sm text-gray-500 text-center">
                Completa la evaluación de desempeño de tu líder
              </p>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">
                No se encontró información de tu líder asignado. Por favor contacta al administrador.
              </p>
            </div>
          )}
        </div>

        {/* Información adicional */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            ¿Necesitas ayuda? Contacta al administrador del sistema
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmpleadoDashboard;
